import { JSX } from 'react';
import { Button, Heading, Layout, Skeleton, Text } from '../../components/core';
import { IconAlert } from '../../components/icons/IconAlert';
import { IconBuilding } from '../../components/icons/IconBuilding';
import { IconHeartHandShake } from '../../components/icons/IconHeartHandShake';
import { IconLifeBuoy } from '../../components/icons/IconLifeBuoy';
import { PageContent, PageFooter, PageHeader } from '../../components/page';
import { ExternalAppointmentWidget } from '../../content/appointment/components/ExternalAppointmentWidget';
import { JoinAppointmentWidget } from '../../content/appointment/components/JoinAppointmentWidget';
import { MobilePromotionBanner } from '../../content/promotion/components/MobilePromotionBanner';
import { View } from '../../content/view/components/View';
import { LoadTrackerProvider } from '../../contexts/loadTrackerContext';
import { getRoute, useNavigate } from '../../routes';
import { getStylesheet } from '../../styles';
import { visuallyHiddenFloatStyle } from '../../utils/accessibility';
import { useTitle } from '../../utils/useTitle';
import { CompletedSurvey } from '../survey/surveyHome/components/CompletedSurvey';
import { IncompleteSurvey } from '../survey/surveyHome/components/IncompleteSurvey';
import { CoachingDashboardCard } from './components/CoachingDashboardCard';
import { CustomModule } from './components/CustomModule';
import { FindCareDashboardCard } from './components/FindCareDashboardCard';
import { ImageSelfCareHand } from './components/ImageSelfCareHand';
import { QuickActionCard } from './components/QuickActionCard';
import { SingleStopCard } from './components/SingleStopCard';
import { StudentReferralCard } from './components/StudentReferralCard';
import { TogetherallCard } from './components/TogetherallCard';
import { UnreadExternalMessagesWidget } from './components/UnreadExternalMessagesWidget';
import { useHomePage } from './hooks/useHomePage';

export const HomePage = (): JSX.Element => {
  return (
    <LoadTrackerProvider>
      <HomePageMain />
    </LoadTrackerProvider>
  );
};

const HomePageMain = (): JSX.Element => {
  useTitle('Home');

  const navigate = useNavigate();

  const { data: homePageData, loading } = useHomePage();

  if (loading) {
    return (
      <PageContent testID="home-page">
        <Skeleton {...styles.pageSkeleton} />
      </PageContent>
    );
  }

  const { orgName, recommendationStatus, getCareCardRoute, show, userGreeting } = homePageData;

  const { survey } = show;

  const onSelfCareClick = (): void => {
    navigate(getRoute('selfCareHome', {}));
  };

  const onViewSurveyResults = (): void => {
    navigate(getRoute('surveyResults', {}));
  };

  return (
    <PageContent testID="home-page">
      <MobilePromotionBanner />

      <PageHeader title={`Welcome${userGreeting}`} />

      <Layout.Box {...styles.topWrapper}>
        <JoinAppointmentWidget />

        <UnreadExternalMessagesWidget />
        <ExternalAppointmentWidget />

        {survey.enabled && survey.data?.status === 'Incomplete' && (
          <IncompleteSurvey
            estimatedCompletionMinutes={survey.data.estimatedCompletionMinutes}
            name={survey.data.name}
          />
        )}

        {show.findCare && <FindCareDashboardCard recommendationStatus={recommendationStatus} />}

        {show.coaching && <CoachingDashboardCard />}

        <Heading.h2 {...visuallyHiddenFloatStyle} level={2}>
          Quick actions
        </Heading.h2>

        <Layout.Flex {...styles.quickActionWrapper}>
          <Layout.Flex {...styles.quickActions} aria-label="Quick actions" role="list">
            {show.getCare && (
              <QuickActionCard
                icon={<IconHeartHandShake size={8} color="primary.600" aria-hidden />}
                ariaTitle="Get Care"
                title="Get Care"
                caption="Get connected with care"
                linkTo={getRoute(getCareCardRoute, {})}
              />
            )}
            {show.myCare && (
              <QuickActionCard
                icon={<IconHeartHandShake size={8} color="primary.600" aria-hidden />}
                ariaTitle="My Care (opens in a new tab)"
                title="My Care"
                caption="Access your care with Mantra"
                linkTo={getRoute('patientPortal', {})}
              />
            )}
            {show.connectNow && (
              <QuickActionCard
                icon={<IconLifeBuoy size={8} color="primary.600" aria-hidden />}
                title="ConnectNow"
                caption="Get support 24/7"
                linkTo={getRoute('connectNowHome', {})}
              />
            )}
          </Layout.Flex>

          <Heading.h2 {...visuallyHiddenFloatStyle} level={2}>
            Resources
          </Heading.h2>

          <Layout.Flex {...styles.quickActions} aria-label="Resources" role="list">
            {show.campusResources && (
              <QuickActionCard
                icon={<IconBuilding size={8} color="primary.600" aria-hidden />}
                title="Campus Resources"
                caption="Find support on campus"
                linkTo={getRoute('campusResources', {})}
              />
            )}

            <QuickActionCard
              icon={<IconAlert size={8} color="red.600" aria-hidden />}
              title="Crisis Resources"
              caption="Emergency Resources"
              linkTo={getRoute('crisisResources', {})}
            />
          </Layout.Flex>
        </Layout.Flex>

        {survey.enabled && survey.data?.status === 'Completed' && (
          <CompletedSurvey name={survey.data.name} onViewResults={onViewSurveyResults} />
        )}

        {show.studentReferral && (
          <>
            <Heading.h2 {...visuallyHiddenFloatStyle} level={2}>
              Refer a friend
            </Heading.h2>

            <StudentReferralCard orgName={orgName} />
          </>
        )}

        {show.skills && (
          <Layout.Shift {...styles.selfCareBox} alignNonMobile="center">
            <ImageSelfCareHand aria-hidden size="160px" />

            <Layout.VStack {...styles.selfCareTopStack} space={8}>
              <Layout.VStack space={3}>
                <Heading.h4 color="lightText" id="self-care-card-header" level={2}>
                  Self Care
                </Heading.h4>

                <Heading.h3 color="lightText">
                  Get the tools to deal with whatever comes your way.
                </Heading.h3>

                <Text.para color="lightText">
                  Quick, practical, evidenced-based skills to help you feel better and handle any
                  situation.
                </Text.para>
              </Layout.VStack>

              <Button.primaryMedium
                {...styles.browseButton}
                aria-label="Browse Self Care skills"
                isLink
                testID="button-home-go-to-self-care"
                onPress={onSelfCareClick}
              >
                Browse Skills
              </Button.primaryMedium>
            </Layout.VStack>
          </Layout.Shift>
        )}

        <CustomModule />

        {(show.otherServices.singleStop.enabled || show.otherServices.togetherAll) && (
          <>
            <Heading.h4 level={2}>Other services available to you.</Heading.h4>

            {show.otherServices.singleStop.enabled && (
              <SingleStopCard
                headingLevel={3}
                linkUrl={show.otherServices.singleStop.url} // Pass the URL only if enabled
              />
            )}

            {show.otherServices.togetherAll && <TogetherallCard headingLevel={3} />}
          </>
        )}

        {show.skills && (
          <Layout.VStack {...styles.learn}>
            <View isCollection hideHeader />
          </Layout.VStack>
        )}
      </Layout.Box>

      <PageFooter />
    </PageContent>
  );
};

const styles = getStylesheet({
  pageSkeleton: {
    height: '100vh',
  },

  topWrapper: {
    gap: 4,
  },

  quickActionWrapper: {
    flex: 1,
    gap: 4,
  },

  selfCareBox: {
    borderRadius: '8px',
    backgroundColor: 'secondary.900',
    gap: 8,
    padding: 8,
  },

  selfCareTopStack: {
    flex: 1,
  },

  browseButton: {
    maxWidth: '145px',
    _web: {
      maxWidth: '133px',
    },
  },

  quickActions: {
    flexDirection: 'row',
    flexGrow: 1,
    flexWrap: 'wrap',
    gap: 2,
    justifyContent: 'space-between',
    width: '100%',
  },

  learn: {
    justifyContent: 'center',
  },
});
